import React from "react";
import { Box, Button, Card, Chip, Stack, Typography } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ComplianceBreadcrumb from "../compliance/ComplianceBreadcrumb";
import { downloadPDF } from "../hipaa/HipaaDetail";
import FilterableTable from "../../shared/Tables/FilterableTable";
import StatCard from "./StatCard";
import {
  activeModels,
  blindspotCards,
  patternLog,
  remediationRoadmap,
  summaryCards,
} from "./aiContextualizationData";
import styles from "./aiContextualizationReport.module.scss";
import { AlertTriangle, Cpu, Radar, ShieldCheck, Sparkles } from "lucide-react";

const iconMap = [Radar, ShieldCheck, AlertTriangle, Cpu];

const AIContextualizationReport = () => {
  const [dateQuery, setDateQuery] = React.useState("");

  const filteredPatternLog = React.useMemo(() => {
    if (!dateQuery) return patternLog;
    return patternLog.filter((row) => row.date === dateQuery);
  }, [dateQuery]);

  return (
    <Box className={styles.page}>
      <div className={styles.topBar}>
        <ComplianceBreadcrumb
          items={[
            { label: "CISO Dashboard", href: "/" },
            { label: "AI Contextualization Report" },
          ]}
        />
        <Button
          variant="contained"
          startIcon={<DownloadOutlinedIcon />}
          className={styles.exportButton}
          onClick={() => downloadPDF("AI-Contextualization-Report")}
        >
          Export Report
        </Button>
      </div>

      <Stack gap={2}>
        <div className={styles.statGrid}>
          {summaryCards.map((card, index) => (
            <StatCard
              key={card.title}
              icon={iconMap[index]}
              title={card.title}
              value={card.value}
              meta={card.meta}
              progress={card.progress}
              // delta={card.delta}
              status={card.status}
            />
          ))}
        </div>

        <div className={styles.sectionGrid}>
          <Card className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
              <div>
                <Typography className={styles.sectionTitle}>
                  The 12% Blindspot
                </Typography>
                <div className={styles.sectionSubtitle}>
                  Uncovered attack pattern categories
                </div>
              </div>
              <div className={styles.sectionActions}>
                <Chip
                  label="150 gaps"
                  size="small"
                  className={styles.gapChip}
                />
                <Button
                  size="small"
                  variant="outlined"
                  className={styles.aiButton}
                  startIcon={<Sparkles size={14} />}
                >
                  Summarize with AI
                </Button>
              </div>
            </div>
            <div className={styles.blindspotList}>
              {blindspotCards.map((item) => (
                <div key={item.title} className={styles.blindspotCard}>
                  <div className={styles.blindspotHeader}>
                    <div className={styles.blindspotTitle}>{item.title}</div>
                    <div className={styles.blindspotMeta}>
                      <span className={styles.blindspotTag}>{item.tag}</span>
                      <span>{item.count}</span>
                    </div>
                  </div>
                  <div className={styles.blindspotLabel}>Risk Intensity</div>
                  <div className={styles.riskBar}>
                    <span style={{ width: `${item.intensity}%` }} />
                  </div>
                  <div className={styles.blindspotDesc}>{item.summary}</div>
                  <div className={styles.blindspotTags}>
                    {item.tags.map((tag) => (
                      <span key={tag} className={styles.tagPill}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
              <div>
                <Typography className={styles.sectionTitle}>
                  Top Active Models
                </Typography>
                <div className={styles.sectionSubtitle}>Ranked by accuracy</div>
              </div>
              <Button
                size="small"
                variant="outlined"
                className={styles.aiButton}
                startIcon={<Sparkles size={14} />}
              >
                Summarize with AI
              </Button>
            </div>
            <div className={styles.modelsTable}>
              <div className={styles.modelsHeader}>
                <span>Model</span>
                <span>Accuracy</span>
                <span>Status</span>
              </div>
              {activeModels.map((model) => (
                <div key={model.name} className={styles.modelsRow}>
                  <div>
                    <div className={styles.modelName}>{model.name}</div>
                    <div className={styles.modelMeta}>{model.meta}</div>
                  </div>
                  <div className={styles.modelAccuracy}>{model.accuracy}</div>
                  <div
                    className={
                      model.status === "warning"
                        ? styles.modelStatusWarning
                        : styles.modelStatusOk
                    }
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className={styles.roadmapSection}>
          <Typography className={styles.sectionTitle}>
            Remediation Roadmap
          </Typography>
          <div className={styles.roadmapGrid}>
            {remediationRoadmap.map((phase) => (
              <Card
                key={phase.title}
                className={`${styles.roadmapCard} ${
                  phase.tone === "primary" ? styles.roadmapPrimary : ""
                }`}
              >
                <div className={styles.roadmapHeader}>
                  <div className={styles.roadmapBadges}>
                    <span className={styles.phaseBadge}>{phase.phase}</span>
                    <span className={styles.phaseTime}>{phase.timeline}</span>
                  </div>
                  {typeof phase.progress === "number" ? (
                    <span className={styles.phaseStatus}>IN PROGRESS</span>
                  ) : null}
                </div>
                <div className={styles.roadmapTitle}>{phase.title}</div>
                <div className={styles.roadmapDesc}>{phase.summary}</div>
                {typeof phase.progress === "number" ? (
                  <div className={styles.roadmapProgressWrap}>
                    <div className={styles.roadmapProgressLabel}>Progress</div>
                    <div className={styles.roadmapProgressTrack}>
                      <span style={{ width: `${phase.progress}%` }} />
                    </div>
                    <div className={styles.roadmapProgressValue}>
                      {phase.progress}%
                    </div>
                  </div>
                ) : null}
                <ul className={styles.roadmapList}>
                  {phase.items.map((item) => (
                    <li key={item} className={styles.roadmapItem}>
                      <span className={styles.roadmapBullet} />
                      {item}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>

        <FilterableTable
          title="Technical Pattern Log"
          data={filteredPatternLog}
          enableDownload
          downloadFilename="technical-pattern-log.csv"
          columns={[
            {
              id: "id",
              label: "Pattern ID",
              headClassName: styles.tableHead,
            },
            {
              id: "detProb",
              label: "Det. Prob.",
              headClassName: styles.tableHead,
              renderCell: (row) => (
                <span className={styles.detProb}>{row.detProb}</span>
              ),
            },
            {
              id: "model",
              label: "Model",
              headClassName: styles.tableHead,
              renderCell: (row) => (
                <span
                  className={
                    row.model === "Pending" ? styles.pendingStatus : undefined
                  }
                >
                  {row.model}
                </span>
              ),
            },
            {
              id: "gapReason",
              label: "Gap Reason",
              headClassName: styles.tableHead,
            },
            {
              id: "category",
              label: "Category",
              headClassName: styles.tableHead,
              renderCell: (row) => (
                <span className={styles.categoryChip}>{row.category}</span>
              ),
            },
          ]}
          searchKeys={["id"]}
          searchPlaceholder="Search by Pattern ID"
          headerActions={
            <input
              type="date"
              value={dateQuery}
              onChange={(event) => setDateQuery(event.target.value)}
              className={styles.dateInput}
            />
          }
          emptyMessage="No patterns match the current filters."
          rowsPerPageOptions={[5, 10, 25]}
          classNames={{
            card: styles.tableCard,
            tableHeader: styles.tableHeader,
            tableHeaderActions: styles.tableHeaderActions,
            sectionTitle: styles.sectionTitle,
            searchField: styles.searchField,
            filterRow: styles.filterRow,
            filterGroup: styles.filterGroup,
            filterLabel: styles.filterLabel,
            filterSelect: styles.filterSelect,
            clearFilters: styles.clearFilters,
            tableContainer: styles.tableContainer,
            tableHead: styles.tableHead,
            tablePagination: styles.tablePagination,
            emptyState: styles.emptyState,
          }}
        />
      </Stack>
    </Box>
  );
};

export default AIContextualizationReport;
